export interface UTMParams {
	utm_source?: string;
	utm_medium?: string;
	utm_campaign?: string;
	utm_content?: string;
	utm_term?: string;
	referrer?: string;
	landing_page: string;
	timestamp: string;
}

const UTM_COOKIE_NAME = "automint_utm";
const UTM_COOKIE_MAX_AGE = 90 * 24 * 60 * 60; // 90 days

/**
 * Extract UTM parameters from current URL
 */
export function extractUTMFromURL(): UTMParams | null {
	if (typeof window === "undefined") return null;

	const url = new URL(window.location.href);
	const params = url.searchParams;

	const hasUTM =
		params.has("utm_source") ||
		params.has("utm_campaign") ||
		params.has("utm_medium");

	if (!hasUTM) return null;

	return {
		utm_source: params.get("utm_source") || undefined,
		utm_medium: params.get("utm_medium") || undefined,
		utm_campaign: params.get("utm_campaign") || undefined,
		utm_content: params.get("utm_content") || undefined,
		utm_term: params.get("utm_term") || undefined,
		referrer: document.referrer || undefined,
		landing_page: window.location.pathname,
		timestamp: new Date().toISOString(),
	};
}

/**
 * Check if UTM parameters exist in URL
 */
export function hasUTMInURL(): boolean {
	if (typeof window === "undefined") return false;

	const url = new URL(window.location.href);
	return (
		url.searchParams.has("utm_source") ||
		url.searchParams.has("utm_campaign") ||
		url.searchParams.has("utm_medium")
	);
}

/**
 * Store UTM parameters in cookie
 */
export function setUTMCookie(utmData: UTMParams): void {
	if (typeof document === "undefined") return;

	const cookieValue = encodeURIComponent(JSON.stringify(utmData));
	document.cookie = `${UTM_COOKIE_NAME}=${cookieValue}; path=/; max-age=${UTM_COOKIE_MAX_AGE}; SameSite=Lax`;
}

/**
 * Get UTM parameters from cookie
 */
export function getUTMCookie(): UTMParams | null {
	if (typeof document === "undefined") return null;

	const cookies = document.cookie.split(";");
	const utmCookie = cookies.find((c) =>
		c.trim().startsWith(`${UTM_COOKIE_NAME}=`),
	);

	if (!utmCookie) return null;

	try {
		const cookieValue = utmCookie.split("=")[1];
		return JSON.parse(decodeURIComponent(cookieValue));
	} catch {
		return null;
	}
}

/**
 * Get UTM from URL or cookie (URL takes priority, then updates cookie)
 * This is the main function to use everywhere
 */
export function getUTMData(): UTMParams | null {
	if (typeof window === "undefined") return null;

	// Priority 1: Check URL for new UTM parameters
	const urlUTM = extractUTMFromURL();
	if (urlUTM) {
		setUTMCookie(urlUTM); // Update cookie with latest UTM
		return urlUTM;
	}

	// Priority 2: Fall back to cookie
	return getUTMCookie();
}
